import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock, Eye, EyeOff, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function AuthPage({ defaultMode = "login" }) {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // `?next=` is an internal-only redirect target (e.g. /accept-invite?token=…).
  // We only honour same-origin paths to prevent open-redirect abuse.
  const rawNext = searchParams.get("next") || "";
  const next = rawNext.startsWith("/") && !rawNext.startsWith("//") ? rawNext : "";
  const [mode, setMode] = useState(defaultMode);
  const [name, setName] = useState("");
  const [workspaceName, setWorkspaceName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [pendingEmail, setPendingEmail] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        const data = await login(email, password);
        // Honour `?next=` if present (e.g. coming from an invite link); else
        // default by role.
        const fallback = data?.global_role === "super_admin" ? "/admin/customers" : "/";
        navigate(next || fallback);
      } else {
        const data = await register(name, email, password, workspaceName);
        if (data?.pending_approval) {
          setPendingEmail(data.email || email);
        } else {
          navigate("/");
        }
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const resetForms = () => {
    setMode("login");
    setName("");
    setWorkspaceName("");
    setEmail("");
    setPassword("");
    setError("");
    setPendingEmail("");
  };

  return (
    <div className="flex items-center justify-center px-4 sm:px-6 py-16 sm:py-24 bg-white dark:bg-[#0B0B0C] min-h-[calc(100vh-220px)]">
      <div
        className="w-full max-w-md bg-white dark:bg-[#0F0F11] border border-[#E5E7EB] dark:border-white/[0.08] rounded-2xl shadow-[0_4px_24px_-12px_rgba(15,23,42,0.08)] dark:shadow-[0_4px_24px_-12px_rgba(0,0,0,0.6)] p-8"
        data-testid="auth-card"
      >
        {pendingEmail ? (
          <div data-testid="register-pending-screen" className="text-center space-y-5">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#3B82F6]/10 text-[#3B82F6]">
              <Clock size={22} />
            </div>
            <p className="font-mono text-[10px] tracking-[0.22em] text-[#6B7280] dark:text-white/55 uppercase">
              Account created · Awaiting approval
            </p>
            <h2 className="font-['Geist'] font-semibold text-2xl tracking-tight text-[#0F172A] dark:text-white">
              Thanks for signing up.
            </h2>
            <p className="text-sm text-[#475569] dark:text-white/70 leading-relaxed">
              We&apos;ve created your account for{" "}
              <span className="font-mono text-[12px] text-[#0F172A] dark:text-white bg-[#F1F5F9] dark:bg-white/10 px-1.5 py-0.5 rounded break-all">
                {pendingEmail}
              </span>
              . An administrator will review and activate it shortly. You&apos;ll be able to sign in as
              soon as it&apos;s approved.
            </p>
            <div className="border border-[#E5E7EB] dark:border-white/10 bg-[#F9FAFB] dark:bg-white/[0.04] rounded-md p-3 text-left flex items-start gap-2.5">
              <CheckCircle2 size={14} className="text-[#3B82F6] mt-0.5 shrink-0" />
              <p className="text-xs text-[#475569] dark:text-white/70">
                No further action required from you right now. Keep your password handy — we&apos;ll
                use it the moment your account is activated.
              </p>
            </div>
            <button
              type="button"
              onClick={resetForms}
              data-testid="register-pending-back-to-signin"
              className="w-full bg-[#3B82F6] text-white py-3 rounded-md text-sm font-medium hover:bg-[#2563EB] transition-colors duration-150"
            >
              Back to sign in
            </button>
          </div>
        ) : (
          <>
            <div className="flex p-1 bg-[#F1F5F9] dark:bg-white/[0.06] rounded-md mb-8">
              <button
                data-testid="login-tab"
                onClick={() => {
                  setMode("login");
                  setError("");
                }}
                className={`flex-1 py-2 text-xs font-semibold tracking-[0.12em] rounded transition-colors duration-150 ${
                  mode === "login"
                    ? "bg-white dark:bg-[#1F1F22] text-[#0F172A] dark:text-white shadow-sm"
                    : "bg-transparent text-[#64748B] dark:text-white/60 hover:text-[#0F172A] dark:hover:text-white"
                }`}
              >
                SIGN IN
              </button>
              <button
                data-testid="register-tab"
                onClick={() => {
                  setMode("register");
                  setError("");
                }}
                className={`flex-1 py-2 text-xs font-semibold tracking-[0.12em] rounded transition-colors duration-150 ${
                  mode === "register"
                    ? "bg-white dark:bg-[#1F1F22] text-[#0F172A] dark:text-white shadow-sm"
                    : "bg-transparent text-[#64748B] dark:text-white/60 hover:text-[#0F172A] dark:hover:text-white"
                }`}
              >
                CREATE ACCOUNT
              </button>
            </div>

            <h2 className="font-['Geist'] font-semibold text-2xl tracking-tight text-[#0F172A] dark:text-white mb-1">
              {mode === "login" ? "Welcome back" : "Start testing in minutes"}
            </h2>
            <p className="text-sm text-[#64748B] dark:text-white/55 mb-6">
              {mode === "login"
                ? "Sign in to your Probe workspace."
                : "Create a workspace — no credit card required."}
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              {mode === "register" && (
                <div className="space-y-1.5">
                  <label className="label-caps">Name</label>
                  <Input
                    data-testid="register-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    autoComplete="name"
                    className="rounded-md border-[#D1D5DB] dark:bg-[#18181B] dark:text-white dark:border-[#27272A] focus-visible:ring-[#3B82F6] focus-visible:border-[#3B82F6]"
                  />
                </div>
              )}
              {mode === "register" && (
                <div className="space-y-1.5">
                  <label className="label-caps">Workspace name</label>
                  <Input
                    data-testid="register-workspace-input"
                    value={workspaceName}
                    onChange={(e) => setWorkspaceName(e.target.value)}
                    required
                    autoComplete="organization"
                    className="rounded-md border-[#D1D5DB] dark:bg-[#18181B] dark:text-white dark:border-[#27272A] focus-visible:ring-[#3B82F6] focus-visible:border-[#3B82F6]"
                  />
                </div>
              )}
              <div className="space-y-1.5">
                <label className="label-caps">Email</label>
                <Input
                  data-testid="auth-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  className="rounded-md border-[#D1D5DB] dark:bg-[#18181B] dark:text-white dark:border-[#27272A] focus-visible:ring-[#3B82F6] focus-visible:border-[#3B82F6]"
                />
              </div>
              <div className="space-y-1.5">
                <label className="label-caps">Password</label>
                <div className="relative">
                  <Input
                    data-testid="auth-password-input"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    autoComplete={mode === "login" ? "current-password" : "new-password"}
                    className="rounded-md border-[#D1D5DB] dark:bg-[#18181B] dark:text-white dark:border-[#27272A] focus-visible:ring-[#3B82F6] focus-visible:border-[#3B82F6] pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    data-testid="auth-password-toggle"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    className="absolute inset-y-0 right-0 flex items-center justify-center w-10 text-[#6B7280] dark:text-white/55 hover:text-[#0F172A] dark:hover:text-white"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              {error && (
                <div
                  className="border border-[#FCA5A5] dark:border-[#7F1D1D] bg-[#FEF2F2] dark:bg-[#3F1D1D] text-[#B91C1C] dark:text-[#FCA5A5] text-sm px-3 py-2 rounded-md"
                  data-testid="auth-error-message"
                >
                  {error}
                </div>
              )}
              <button
                type="submit"
                disabled={loading}
                data-testid="auth-submit-button"
                className="w-full bg-[#3B82F6] text-white py-3 rounded-md text-sm font-medium hover:bg-[#2563EB] transition-colors duration-150 disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {loading && <Loader2 size={14} className="animate-spin" />}
                {mode === "login" ? "Sign in" : "Create account"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
